import Markdown from 'react-markdown'
import { Link } from 'react-router'
import { backState } from '#app/components/back-link.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { cn } from '#app/utils/misc.tsx'

interface Props {
	title: string
	eyebrow?: string
	eyebrowHref?: string
	href?: string
	description: string
	dates: string
	tags: readonly string[]
	link?: string
	image?: string
	imageDark?: string
	video?: string
	links?: readonly {
		icon: React.ReactNode
		type: string
		href: string
	}[]
	className?: string
}

export function ProjectCard({
	title,
	eyebrow,
	eyebrowHref,
	href,
	description,
	dates,
	tags,
	link,
	image,
	imageDark,
	video,
	links,
	className,
}: Props) {
	return (
		<Card
			className={
				'flex h-full flex-col overflow-hidden border transition-all duration-300 ease-out hover:shadow-lg'
			}
		>
			<Link
				to={href || '#'}
				state={backState('/#projects')}
				className={cn('block cursor-pointer', className)}
			>
				{video && (
					<video
						src={video}
						autoPlay
						loop
						muted
						playsInline
						className="pointer-events-none mx-auto aspect-video w-full object-cover object-top"
					/>
				)}
				{image && (
					<img
						src={image}
						alt={title}
						width={500}
						height={300}
						className={cn(
							'aspect-video w-full overflow-hidden object-cover object-top',
							imageDark && 'dark:hidden',
						)}
					/>
				)}
				{imageDark && (
					<img
						src={imageDark}
						alt={title}
						width={500}
						height={300}
						className="hidden aspect-video w-full overflow-hidden object-cover object-top dark:block"
					/>
				)}
			</Link>
			<CardHeader className="px-2">
				<div className="space-y-1">
					{eyebrow &&
						(eyebrowHref ? (
							<Link
								to={eyebrowHref}
								state={backState('/#projects')}
								className="hover:text-foreground text-muted-foreground block font-sans text-[10px] font-medium tracking-wide uppercase underline decoration-dotted underline-offset-2 transition-colors"
							>
								{eyebrow}
							</Link>
						) : (
							<p className="text-muted-foreground font-sans text-[10px] font-medium tracking-wide uppercase">
								{eyebrow}
							</p>
						))}
					<CardTitle className="mt-1 text-base">{title}</CardTitle>
					<time className="font-sans text-xs">{dates}</time>
					<div className="hidden font-sans text-xs underline print:visible">
						{link?.replace('https://', '').replace('www.', '').replace('/', '')}
					</div>
					<div className="prose dark:prose-invert text-muted-foreground mt-1 max-w-full font-sans text-xs text-pretty">
						<Markdown>{description}</Markdown>
					</div>
				</div>
			</CardHeader>
			<CardContent className="mt-auto flex flex-col px-2">
				{tags && tags.length > 0 && (
					<div className="mt-2 flex flex-wrap gap-1">
						{tags?.map((tag) => (
							<Badge
								className="px-1 py-0 text-[10px]"
								variant="secondary"
								key={tag}
							>
								{tag}
							</Badge>
						))}
					</div>
				)}
			</CardContent>
			<CardFooter className="px-2 pb-2">
				{links && links.length > 0 && (
					<div className="flex flex-row flex-wrap items-start gap-1">
						{links?.map((link, idx) => (
							<Link to={link?.href} key={idx} target="_blank">
								<Badge key={idx} className="flex gap-2 px-2 py-1 text-[10px]">
									{link.icon}
									{link.type}
								</Badge>
							</Link>
						))}
					</div>
				)}
			</CardFooter>
		</Card>
	)
}
